import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';

import { DateTimeService } from 'src/app/core/services/date-time.service';
import { BaseFormKitComponent } from '../base/base-form-kit.component';
import { BaseFormKitModule } from '../base/base-form-kit.module';
import { AppDatePickerInputMaxType } from './types/app-date-picker-input-max-type.type';
import { DatePickerMinType } from './types/datepicker-min.type';
import { DatePickerValue } from './types/datepicker-value.type';

@Component({
    selector: 'datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['../base/base-form-kit.scss', './datepicker.component.scss'],
    imports: [BaseFormKitModule, MatDatepickerModule, CommonModule]
})
export class DatepickerComponent
  extends BaseFormKitComponent
  implements OnInit
{
  //#region properties
  #todayDate: Date = new Date();
  #tomorrowDate: Date = new Date();
  _min;
  _max;
  oldValue: any;

  @Input() isEdit: boolean = false;
  @Input() min: any = DateTimeService.getTodayDate();
  @Input() max: any;
  @Input() minType: DatePickerMinType = 'unlimited';
  @Input() maxType: AppDatePickerInputMaxType = 'unlimited';
  @Input() isAdvancedSearch: boolean = false;
  @Input() isShowToggle: boolean = true;
  @Input() invalidDateErrMsg: string = 'messages.invalidDateErrMsg';
  @Input() value: DatePickerValue;
  @Output('value') valueChange = new EventEmitter<any>();
  @Output('onErrorDatePicker') output_onError = new EventEmitter<{
    errorMessage: string;
  }>();
  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.element) {
      const offsetWidth = this.element.nativeElement.offsetWidth;
      if (+offsetWidth < 150) this.isShowToggle = false;
      else this.isShowToggle = true;
    }
  }

  //#endregion

  constructor(
    protected translate: TranslateService,
    private element: ElementRef
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit;
    this.onResize();
    this.oldValue = this.value;
    this.setMin();
    this.setMax();
    this.setDefaultValue();
    this.emit();
    this.checkIsValidData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['maxType'] ||
      changes['minType'] ||
      changes['max'] ||
      changes['min']
    ) {
      this.setMin();
      this.setMax();
      this.checkIsValidData();
    }

    if (changes['value']) {
      this.setDefaultValue();
      this.checkIsValidData();
    }
  }

  //Checks that the selected date is greater than the minimum date :|
  checkIsValidData() {
    try {
      if (!this.value) return;

      const Value = new Date(this.value);
      const invalidDateErrMsg = this.translate.instant(
        'messages.invalidDateErrorMsg'
      );
      let isValid: boolean = true;
      if (this.minType != 'unlimited' && this.minType != undefined) {
        const Min = new Date(this.min);

        if (
          !this.isAdvancedSearch &&
          Min.setHours(0, 0, 0, 0) > Value.setHours(0, 0, 0, 0)
        ) {
          isValid = false;
          this.output_onError.emit({ errorMessage: invalidDateErrMsg });
        }
      }

      if (this.maxType != 'unlimited' && this.maxType != undefined) {
        const Max = new Date(this.max);

        if (
          !this.isAdvancedSearch &&
          Max.setHours(0, 0, 0, 0) < Value.setHours(0, 0, 0, 0)
        ) {
          isValid = false;
          this.output_onError.emit({ errorMessage: invalidDateErrMsg });
        }
      }

      //enable field error => red border:)
      this.control?.markAsTouched();
    } catch (error) {
      console.error('datePicker Error => ', error);
    }
  }

  setMin() {
    try {
      switch (this.minType) {
        case 'today':
          this._min = DateTimeService.getTodayDate();
          break;
        case 'tomorrow':
          this.#tomorrowDate.setDate(this.#todayDate.getDate() + 1);
          this.#tomorrowDate.setHours(0, 0, 0, 0);
          this._min = this.#tomorrowDate;
          break;
        case 'unlimited':
          this._min = '';
          break;
        case 'custom':
          this._min = this.min ? this.min : '';
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('datePicker Error => ', error);
    }
  }

  setMax() {
    try {
      if (this.minType == 'tomorrow' && this.maxType == 'today')
        this._max = 'tomorrow';

      switch (this.maxType) {
        case 'today':
          this._max = DateTimeService.getTodayDate();
          break;
        case 'tomorrow':
          this.#tomorrowDate.setDate(this.#todayDate.getDate() + 1);
          this.#tomorrowDate.setHours(0, 0, 0, 0);
          this._max = this.#tomorrowDate;
          break;
        case 'unlimited':
          this._max = '';
          break;
        case 'custom':
          this._max = this.max ? this.max : '';
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('datePicker Error => ', error);
    }
  }

  setDefaultValue() {
    try {
      switch (this.value) {
        case 'aYearLater':
          this.value = DateTimeService.getAYearLaterUTC();
          break;
        case 'today':
          this.#todayDate.setHours(0);
          this.#todayDate.setMinutes(0);
          this.#todayDate.setSeconds(0);
          this.value = this.#todayDate.toString();
          break;
        case 'tomorrow':
          this.#tomorrowDate.setDate(this.#todayDate.getDate() + 1);
          this.value = this.#tomorrowDate.toString();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('datePicker Error => ', error);
    }
  }

  clearValue() {
    this.value = null;
    this.emit();
  }

  emit() {
    try {
      //to check if the selected date is now expired in edit mode
      if (this.isEdit) {
        const today = DateTimeService.getTodayDate();
        const date = new Date(this.value);
        const oldDate = new Date(this.oldValue);
        const todayDate = new Date(today);

        if (date.getTime() !== oldDate.getTime() && date < todayDate) {
          const invalidDateErrMsg = this.translate.instant(
            'messages.invalidMinDate'
          );
          this.output_onError.emit({ errorMessage: invalidDateErrMsg });

          this.valueChange.emit(oldDate);
        } else {
          if (this.value?._d) this.valueChange.emit(this.value?._d);
          else this.valueChange.emit(this.value);
        }
      } else {
        if (this.value?._d) this.valueChange.emit(this.value?._d);
        else this.valueChange.emit(this.value);
      }
    } catch (error) {
      console.error('datePicker Error => ', error);
    }
  }
}
